import React, { forwardRef } from "react";
import {
    AriaButtonOptions,
    VisuallyHidden,
    useButton,
    useFocusRing,
    useObjectRef,
} from "react-aria";
import { ButtonContext, useSlottedContext } from "react-aria-components";

import { SimpleLinkBlock } from "@reactivated";

import { Clickable as BaseClickable } from "@thelabnyc/thelabui/src/components/Clickable";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { SVGName, Svg, allSvgNames } from "../Svg";

import styles from "./index.module.scss";

export type ClickableProps = (
    | React.ComponentProps<"a">
    | React.ComponentProps<"button">
) &
    AriaButtonOptions<"button" | "a"> & {
        simpleLink?: SimpleLinkBlock;
        /**
         * If a className is provided, it will be placed in a div that will
         * otherwise not exist
         */
        innerClassName?: string;
        icon?: (typeof allSvgNames)[number] | Exclude<React.ReactNode, string>;
    };

const simpleLinkBlock = (props: SimpleLinkBlock | undefined) => {
    if (!props) return {};
    const target = props.link_type === "external" ? "_blank" : "_self";
    const href = props.url ?? undefined;

    return {
        target,
        href,
    };
};

const Icon = ({ icon }: { icon: React.ReactNode }) => {
    /** Otherwise go get it from the Svg component */
    if (allSvgNames.includes(icon as (typeof allSvgNames)[number]))
        return (
            <Svg
                name={icon as SVGName}
                visuallyHiddenText={null}
                aria-hidden={true}
            />
        );

    return icon;
};

export const Clickable = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(({ icon, ...props }, ref) => {
    ref = useObjectRef(ref);
    const buttonContext = useSlottedContext(ButtonContext, props.slot);
    const { buttonProps } = useButton({ ...props, ...buttonContext }, ref);
    const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);
    const {
        onPress,
        isDisabled,
        simpleLink,
        innerClassName,
        ...propsWithoutOddballs
    } = props;

    const simpleLinkProps = simpleLinkBlock(simpleLink);

    const clickableProps = {
        ...buttonProps,
        ...focusProps,
        ...propsWithoutOddballs,
        ...simpleLinkProps,
    };

    return (
        <BaseClickable
            ref={ref}
            {...clickableProps}
            className={concatClassNames([styles.base, props.className])}
            data-focused={isFocused || undefined}
            data-focus-visible={isFocusVisible || undefined}
        >
            <span className={innerClassName}>
                {props.children ?? simpleLink?.display_name}
            </span>
            <Icon icon={icon} />
        </BaseClickable>
    );
});

export const ButtonPrimary = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function ButtonPrimary(props, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.primaryButton,
                props.className ?? undefined,
            ])}
        >
            {props.children}
        </Clickable>
    );
});

export const TextLink = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps
>(function TextLink(props, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.buttonSecondary,
                props.className ?? undefined,
            ])}
        >
            {props.children}
        </Clickable>
    );
});

export const IconButton = forwardRef<
    HTMLButtonElement | HTMLAnchorElement,
    ClickableProps & { label: string; variant?: "none" | "transparent" }
>(function TextLink({ label, variant = "none", ...props }, ref) {
    return (
        <Clickable
            {...props}
            ref={ref}
            className={concatClassNames([
                styles.iconButton,
                variant == "transparent" ? styles.transparent : "",
                props.className ?? undefined,
            ])}
        >
            {props.children}
            <VisuallyHidden>{label}</VisuallyHidden>
        </Clickable>
    );
});
